/** CSV export modal — columns left, options right. For Media Devoted | Created By Ciro Autiero */

import { useRef, type FC } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

import { Button, Card } from '@/components/ui';
import type { GenericStatsRow } from '../../shared/generic-stats.types';
import type { CsvColumnSpec } from '../buildCsvColumns';
import { useCsvExportModalState } from '../hooks';
import type { ServerCsvExportOptions } from '../hooks/useCsvExportModalState.types';
import type { ServerCsvExportSortConfig } from '../server-sort';
import { CsvExportModalAside } from './aside';
import { CsvExportColumnsSection } from './CsvExportColumnsSection';
import { CsvExportLoadingOverlay } from './CsvExportLoadingOverlay';

interface CsvExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tab: string;
  columns: CsvColumnSpec[];
  rows: GenericStatsRow[];
  userId: string;
  serverCsvExport?: (
    columnKeys: string[],
    sortOverride?: { field: string; dir: 'asc' | 'desc' } | null,
    options?: ServerCsvExportOptions,
  ) => Promise<void>;
  serverCsvSortConfig?: ServerCsvExportSortConfig;
  /** Phone “All” Voluum totalRows — used for export wait-time copy only. */
  serverExportEstimatedRowCount?: number | null;
  csvExportWorkDate?: string;
  canManageTemplates?: boolean;
  controlledExcludeCrossSourceNotedRows?: boolean;
  onControlledExcludeCrossSourceNotedRowsChange?: (value: boolean) => void;
}

export const CsvExportModal: FC<CsvExportModalProps> = (props) => {
  const {
    open,
    onOpenChange,
    columns,
    rows,
    serverCsvExport,
    serverCsvSortConfig,
    serverExportEstimatedRowCount,
  } = props;
  const downloadFilenameInputRef = useRef<HTMLInputElement>(null);
  const s = useCsvExportModalState({ ...props, downloadFilenameInputRef });

  if (!open) return null;
  const hasExportSortOptions = Boolean(serverCsvSortConfig?.options?.length);
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[200]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => {
            if (!s.serverExporting) onOpenChange(false);
          }}
          aria-hidden={s.serverExporting}
        />
        <div className="absolute inset-0 flex items-center justify-center overflow-y-auto p-4 py-8">
          <motion.div
            initial={{ scale: 0.97, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            className="relative z-10 my-auto w-full max-w-5xl"
          >
            <Card className="relative overflow-visible rounded-xl shadow-lg">
              {s.serverExporting ? (
                <CsvExportLoadingOverlay
                  serverExportEstimatedRowCount={serverExportEstimatedRowCount}
                />
              ) : null}
              <header className="border-b border-border/40 px-5 pb-4 pt-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-semibold tracking-tight">Download CSV</h3>
                  <button
                    type="button"
                    disabled={s.serverExporting}
                    onClick={() => onOpenChange(false)}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                  {serverCsvExport
                    ? 'Choose columns and order. The server loads the full Voluum range with your filters, then downloads the CSV (large ranges may take a while).'
                    : hasExportSortOptions
                      ? `Choose columns and order. Export uses the current filtered view (${rows.length} rows). Use row order below for ascending/descending in the file only.`
                      : `Choose columns and order. Export uses the current filtered view (${rows.length} rows).`}
                </p>
              </header>
              <div className="flex max-h-[min(72vh,560px)] flex-col lg:flex-row lg:items-stretch">
                <div className="min-h-0 min-w-0 flex-1 overflow-y-auto border-border/40 px-5 py-4 lg:border-r">
                  <CsvExportColumnsSection
                    columns={columns}
                    exportColumnKeys={s.exportColumnKeys}
                    disabled={s.serverExporting}
                    onKeysChange={s.setExportColumnKeys}
                  />
                </div>
                <CsvExportModalAside
                  hasExportSortOptions={hasExportSortOptions}
                  serverCsvSortConfig={serverCsvSortConfig}
                  isServerCsvExport={Boolean(serverCsvExport)}
                  showCrossSourceExclude={Boolean(serverCsvExport)}
                  exportColumnKeys={s.exportColumnKeys}
                  excludeCrossSourceNotedRows={s.excludeCrossSourceNotedRows}
                  setExcludeCrossSourceNotedRows={s.setExcludeCrossSourceNotedRows}
                  serverExporting={s.serverExporting}
                  exportSortField={s.exportSortField}
                  exportSortDir={s.exportSortDir}
                  setExportSortField={s.setExportSortField}
                  setExportSortDir={s.setExportSortDir}
                  templates={s.templates}
                  editingTemplateId={s.editingTemplateId}
                  canManageTemplates={s.canManageTemplates}
                  templatesBusy={s.templatesBusy}
                  templateName={s.templateName}
                  templateColor={s.templateColor}
                  saveAsDefault={s.saveAsDefault}
                  setTemplateName={s.setTemplateName}
                  setTemplateColor={s.setTemplateColor}
                  setSaveAsDefault={s.setSaveAsDefault}
                  applyTemplate={s.applyTemplate}
                  deleteTemplate={s.deleteTemplate}
                  handleSaveTemplate={s.handleSaveTemplate}
                  handleUpdateTemplate={s.handleUpdateTemplate}
                  exportDownloadBaseName={s.exportDownloadBaseName}
                  onExportDownloadBaseNameChange={s.onExportDownloadBaseNameChange}
                  downloadFilenameInputRef={downloadFilenameInputRef}
                />
              </div>
              <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border/40 px-5 py-4">
                <Button variant="outline" disabled={s.serverExporting} onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button
                  onClick={() => void s.handleDownload()}
                  disabled={s.exportColumnKeys.length === 0 || s.serverExporting}
                  loading={s.serverExporting}
                >
                  {s.serverExporting ? 'Preparing…' : 'Download'}
                </Button>
              </footer>
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
